import { ComponentPropsWithRef, ReactNode } from "react";
import styles from "./DefaultTemplate.module.scss";
import { clsx } from "clsx";
import { useRouteMainFocus } from "../utils/useRouteMainFocus.ts";
import useAccessiblePageTitle from "../../../common/page-title/useAccessiblePageTitle.tsx";
import BaseHeader from "../../../common/header/BaseHeader.tsx";
import ErrorBoundary from "common/core-ui/ErrorBoundary.tsx";

interface DefaultTemplateProps {
  children: ReactNode;
  title: string;
  noInset?: boolean;
  /**
   * Class name for element wrapping the header and main content, but
   * not the panel. A good place for CSS vars that apply to both regions.
   * This region also has a queryable container named `template-content`
   * available.
   */
  contentClassName?: string;
  headerConfig?: ComponentPropsWithRef<typeof BaseHeader>;
  // This is useful to pull the header element outside the main element
  // or null if you want no header
  overrideHeader?: ReactNode;
}

export const rightContentTargetId = "right-content-target";

/**
 * DefaultTemplate is intended for full-width page content with right side
 * panel support.
 */
export default function DefaultTemplate(props: DefaultTemplateProps) {
  const accessiblePageTitle = useAccessiblePageTitle(props.title);

  useRouteMainFocus();

  return (
    <div className={styles.page} id={rightContentTargetId}>
      <div
        className={clsx(
          styles.innerContentNoPanel,
          props.noInset && styles.noInset
        )}
      >
        <div className={props.contentClassName}>
          {props.overrideHeader !== undefined ? (
            props.overrideHeader
          ) : (
            <BaseHeader {...props?.headerConfig} />
          )}
          <main
            aria-label={accessiblePageTitle}
            className={clsx(styles.primaryContent)}
          >
            <ErrorBoundary>{props.children}</ErrorBoundary>
          </main>
        </div>
      </div>
      {/* Panel goes here, flexible size/layout/content/etc. Single element only. */}
    </div>
  );
}
